import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FaPlus, FaTrash, FaTimes } from 'react-icons/fa';

const ProductManager = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        name: '', sku: '', price: '', stock_quantity: '', category_id: '', description: '', images: null
    });

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, []);

    const fetchProducts = async () => {
        try {
            const res = await api.get('/products');
            setProducts(res.data);
        } catch (err) { toast.error("Failed to load products"); }
    };

    const fetchCategories = async () => {
        try {
            const res = await api.get('/categories');
            setCategories(res.data);
        } catch (err) { console.error("Error loading categories"); }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFileChange = (e) => {
        setFormData({ ...formData, images: e.target.files });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        // Use FormData for Multipart/form-data
        const data = new FormData();
        data.append('name', formData.name);
        data.append('sku', formData.sku);
        data.append('price', formData.price);
        data.append('stock_quantity', formData.stock_quantity);
        data.append('category_id', formData.category_id);
        data.append('description', formData.description);
        
        // Append all selected files
        if (formData.images) {
            for (let i = 0; i < formData.images.length; i++) {
                data.append('images', formData.images[i]);
            }
        }

        try {
            await api.post('/products', data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success("Product created successfully!");
            setShowForm(false);
            fetchProducts(); // Refresh list
            // Reset form
            setFormData({ name: '', sku: '', price: '', stock_quantity: '', category_id: '', description: '', images: null });
        } catch (err) {
            console.error(err);
            toast.error("Error creating product");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if(!window.confirm("Are you sure you want to delete this product?")) return;
        try {
            await api.delete(`/products/${id}`);
            toast.success("Product deleted");
            setProducts(products.filter(p => p.id !== id));
        } catch (err) { toast.error("Delete failed"); }
    };

    return (
        <div>
            <div className="admin-header">
                <h2>Product Management</h2>
                <button className="admin-btn" onClick={() => setShowForm(!showForm)}>
                    {showForm ? <><FaTimes /> Cancel</> : <><FaPlus /> Add Product</>}
                </button>
            </div>

            {/* ADD PRODUCT FORM */}
            {showForm && (
                <div className="admin-form-container">
                    <form onSubmit={handleSubmit}>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Product Name</label>
                                <input type="text" name="name" className="form-control" onChange={handleInputChange} required />
                            </div>
                            <div className="form-group">
                                <label>SKU</label>
                                <input type="text" name="sku" className="form-control" onChange={handleInputChange} required />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Price (KSh)</label>
                                <input type="number" name="price" className="form-control" onChange={handleInputChange} required />
                            </div>
                            <div className="form-group">
                                <label>Stock Quantity</label>
                                <input type="number" name="stock_quantity" className="form-control" onChange={handleInputChange} required />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Category</label>
                                <select name="category_id" className="form-control" onChange={handleInputChange} required>
                                    <option value="">Select Category</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Images (Select Multiple)</label>
                                <input type="file" multiple className="form-control" onChange={handleFileChange} required />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Description</label>
                            <textarea name="description" className="form-control" rows="3" onChange={handleInputChange}></textarea>
                        </div>

                        <button type="submit" className="admin-btn" disabled={loading}>
                            {loading ? "Uploading..." : "Save Product"}
                        </button>
                    </form>
                </div>
            )}

            {/* PRODUCTS TABLE */}
            <table className="admin-table">
                <thead>
                    <tr>
                        <th>Image</th>
                        <th>Name</th>
                        <th>SKU</th>
                        <th>Category</th>
                        <th>Price</th>
                        <th>Stock</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {products.map(product => (
                        <tr key={product.id}>
                            <td>
                                {product.images && product.images.length > 0 ? (
                                    <img src={product.images[0]} alt="product" style={{width:'40px', height:'40px', objectFit:'cover', borderRadius:'4px'}} />
                                ) : (
                                    <div style={{width:'40px', height:'40px', background:'#333', borderRadius:'4px'}}></div>
                                )}
                            </td>
                            <td>{product.name}</td>
                            <td>{product.sku}</td>
                            <td>{product.category?.name || 'N/A'}</td>
                            <td>KSh {product.price}</td>
                            <td>
                                <span style={{color: product.stock_quantity > 0 ? '#10b981' : '#ef4444'}}>
                                    {product.stock_quantity}
                                </span>
                            </td>
                            <td>
                                <button className="action-btn delete-btn" onClick={() => handleDelete(product.id)}>
                                    <FaTrash />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default ProductManager;